import React from "react";
import { Loading } from "../../common/Table/Loading";
import cx from "classnames";
import { IInventory, IPaginationMeta } from "../../../@types";
import { useInView } from "react-intersection-observer";
import { JsxElement } from "typescript";
import { TableHead } from "../../../@types/inventory";
import Empty from "../../common/Table/Empty";
import EmptyArchive from "../archive/EmptyArchive";

export type TableItem = {
  [key: string]:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
};

type BodyType = {
  inventory?: IInventory[];
  data: TableItem[];
  isLoading: boolean;
  showPagination: boolean;
  tableHead: any;
  meta?: IPaginationMeta;
  emptyText?: string;
  fetchMoreData?: (direction: "up" | "down") => void | undefined;
};

const ListInventoryBody = ({
  data,
  isLoading,
  showPagination,
  tableHead,
  meta,
  fetchMoreData,
  emptyText,
}: BodyType) => {
  const { ref, inView } = useInView({
    threshold: 0,
  });

  const [showTooltip, setShowTooltip] = React.useState(false);
  const [tooltipContent, setTooltipContent] = React.useState<
    | string
    | number
    | boolean
    | undefined
    | null
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal
  >("");

  React.useEffect(() => {
    if (inView && showPagination && meta?.hasMoreData && fetchMoreData) {
      fetchMoreData("down");
    }
  }, [inView, showPagination, meta?.hasMoreData]);

  return (
    <div className="mt-6 overflow-x-auto rounded-[10px]">
      <table className="w-full font-medium text-[14px]">
        <thead className="h-[43px] !font-medium bg-[#F9FAFB] border-b-2 border-gray-200">
          <tr className=" whitespace-nowrap">
            {tableHead.map((head: any) => (
              <th
                key={head.dataIndex}
                className={`py-3 px-2 !font-medium  ${head?.className}`}
              >
                {head.title || head.icon}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="font-medium capitalize  text-[16px] ">
          {data?.length === 0 ? (
            <tr className="!border-b capitalize">
              <td colSpan={tableHead.length}>
                <EmptyArchive EmptyText={emptyText} />
              </td>
            </tr>
          ) : (
            data?.map((dat, i) => {
              if (data?.length === i + 1) {
                return (
                  <tr
                    ref={ref}
                    key={i}
                    className={cx({
                      "bg-white dark:border-gray-700 dark:bg-gray-800 border-b capitalize  h-[58px] whitespace-nowrap":
                        i % 2 === 0,
                      "border-b  bg-opacity-10 dark:border-gray-700 dark:bg-gray-800 capitalize  h-[88px] whitespace-nowrap":
                        i % 2 !== 0,
                    })}
                  >
                    {tableHead.map((item: any, i: any) => (
                      <td
                        key={`${item.name}${i}`}
                        onMouseEnter={() => {
                          setShowTooltip(true);
                          setTooltipContent(dat["reason1"]);
                        }}
                        onMouseLeave={() => setShowTooltip(false)}
                        onClick={() => {
                          setShowTooltip(true);
                          setTooltipContent(dat["reason1"]);
                        }}
                        className={`py-3 px-2 text-center ${item.className}`}
                      >
                        {item.dataIndex === "reason" && showTooltip ? (
                          <div className="relative">
                                <span
                      className={cx(
                        "px-2 py-1 rounded", // Adjust padding if needed
                        item.dataIndex === "status" && {
                          "border-l-4 border-green-500 pl-2": dat[item.dataIndex] === "active",
                          "border-l-4 border-red-500 pl-2": dat[item.dataIndex] === "expired",
                        }
                      )}
                    >
                           {dat[item.dataIndex]}
                           </span>
                            <span className="absolute bg-white p-2 top-[-28px] whitespace-normal right-6 w-full text-[16px] z-50 shadow-lg rounded-[4px]">
                              {tooltipContent}
                            </span>
                          </div>
                        ) : (
                          <span
                          className={cx(
                            "px-2 py-1 rounded", // Adjust padding if needed
                            item.dataIndex === "status" && {
                              "border border-green-500 text-green-600 pl-2": dat[item.dataIndex] === "active",
                              "border border-red-500 pl-2 text-red-600": dat[item.dataIndex] === "expired",
                            }
                          )}
                        >
                          {dat[item.dataIndex]}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              } else {
                return (
                  <tr
                    key={i}
                    className={cx({
                      "bg-white dark:border-gray-700 dark:bg-gray-800 border-b h-[58px] whitespace-nowrap":
                        i % 2 === 0,
                      "border-b bg-opacity-10 dark:border-gray-700 dark:bg-gray-800 h-[88px] whitespace-nowrap":
                        i % 2 !== 0,
                    })}
                  >
                    {tableHead.map((item: any, i: any) => (
                      <td
                        key={`${item.name}${i}`}
                        className={`py-3 px-2 text-center ${item.className}`}
                      >
                      <span
                      className={cx(
                        "px-2 py-1 rounded", // Adjust padding if needed
                        item.dataIndex === "status" && {
                          "border border-green-500 text-green-600 pl-2 capitalize": dat[item.dataIndex] === "active",
                          "border border-red-500 pl-2 text-red-600 capitalize": dat[item.dataIndex] === "expired",
                          "capitalize": dat[item.dataIndex] === "cancelled"
                        }
                      )}
                    >
                       {dat[item.dataIndex]}
                    </span>
                       
                      </td>
                    ))}
                  </tr>
                );
              }
            })
          )}
          {isLoading && <Loading data={tableHead} />}
        </tbody>
      </table>
      <div className="mb-[56px]"></div>
    </div>
  );
};

export default React.memo(ListInventoryBody);
